import React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';

import type {
  TabsProps,
  TabsListProps,
  TabsTriggerProps,
  TabsContentProps,
} from '@radix-ui/react-tabs';

import { cn } from '@ui/lib/utils';

import { TabLink, type TabLinkProps } from './TabLink';

const tabsRootVariants = cva('size-full flex', {
  variants: {
    variant: {
      primary: 'gap-md tablet:gap-lg',
      secondary: 'gap-md tablet:gap-lg',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export const Root = React.forwardRef<
  HTMLDivElement,
  TabsProps & VariantProps<typeof tabsRootVariants>
>(({ children, className, variant, ...rest }, ref) => (
  <TabsPrimitive.Root
    ref={ref}
    className={cn(tabsRootVariants({ variant }), className)}
    {...rest}
  >
    {children}
  </TabsPrimitive.Root>
));
Root.displayName = 'Root';

export const List = React.forwardRef<HTMLDivElement, TabsListProps>(
  ({ children, className, ...rest }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn('min-h-xxl flex w-full', className)}
      {...rest}
    >
      {children}
    </TabsPrimitive.List>
  ),
);
List.displayName = 'List';

export const Trigger = React.forwardRef<
  HTMLButtonElement,
  TabsTriggerProps & TabLinkProps
>(({ children, className, value, variant, ...rest }, ref) => (
  <TabsPrimitive.Trigger ref={ref} value={value} asChild {...rest}>
    <TabLink variant={variant} className={className}>
      {children}
    </TabLink>
  </TabsPrimitive.Trigger>
));
Trigger.displayName = 'Trigger';

export const Content = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ children, className, ...rest }, ref) => (
    <TabsPrimitive.Content ref={ref} className={className} {...rest}>
      {children}
    </TabsPrimitive.Content>
  ),
);
Content.displayName = 'Content';
